import { InternalLogMessage, LogLevel, LogMessageMetadata } from '../types.js';

import { BatchLogTransport } from './utils/batch.js';

export interface ConsoleLogTransportOptions {
  timeout?: number;
}

export class BrowserConsoleLogTransport extends BatchLogTransport {
  // Save these in case the console is hijacked later
  private $debug = console.debug;
  private $info = console.info;
  private $warn = console.warn;
  private $error = console.error;
  private $log = console.log;

  constructor(options?: ConsoleLogTransportOptions) {
    super(options?.timeout ?? 200);
  }

  formatTimestamp(ts: number) {
    const date = new Date(ts);
    return (
      `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:` +
      `${date.getSeconds().toString().padStart(2, '0')}.${date.getMilliseconds().toString().padStart(3, '0')}`
    );
  }

  format({ level, logger, message, ts, ...metadata }: InternalLogMessage): [string, LogMessageMetadata] {
    return [
      `${this.formatTimestamp(ts)} - ${LogLevel[level].padEnd(5, ' ').toUpperCase()} - ${logger} - ${message}`,
      metadata,
    ];
  }

  async logBatch(logs: Array<[LogLevel, InternalLogMessage]>): Promise<void> {
    // We want to make sure messages are logged in the appropriate order so we do it in a worker
    try {
      for (let i = 0; i < logs.length; i++) {
        // This is a tremendously inefficient way of consuming the queue but it's simple and interruptible
        const [_, message] = logs[i];
        const level = message.level;
        const [line, metadata] = this.format(message);
        switch (level) {
          case LogLevel.trace:
          case LogLevel.debug:
            this.$debug(line, metadata);
            break;
          case LogLevel.info:
            this.$info(line, metadata);
            break;
          case LogLevel.warn:
            this.$warn(line, metadata);
            break;
          case LogLevel.error:
            this.$error(line, metadata);
            break;
          default:
            this.$log(line, metadata);
        }
      }
    } catch (err) {
      console.error('ConsoleLogTransport - Failure', err);
    }
  }
}
